/**
 * StashThumbnailGrid - Fixed-aspect grid layout for StashThumbnail items
 *
 * Renders items in a responsive CSS grid with configurable column counts.
 * Supports edit mode (multi-select) and custom overlays per item.
 *
 * For custom overlays (processing indicators, tag badges, etc.), use:
 * - getItemOverlay: Returns ReactNode for each post
 * - getItemClassName: Returns additional classes for each post
 *
 * These work with ThumbnailOverlayBadge for consistent styling.
 */

import { memo, useMemo } from "react";
import { cn } from "@/lib/utils";
import { usePreloadStashes } from "@/hooks/usePreloadStashes";
import {
  StashThumbnail,
  type StashThumbnailData,
  type ThumbnailRadius,
} from "./StashThumbnail";
import { buildThumbnailItemProps, type ThumbnailSelectProps } from "./types";
import "./StashGallery.css";
import { CTA, META } from "@/lib/typography";

export interface StashThumbnailGridProps extends ThumbnailSelectProps {
  posts: StashThumbnailData[];
  className?: string;
  emptyMessage?: string;
  /** Extra node rendered as the last grid cell (e.g. CTA card) */
  appendNode?: React.ReactNode;

  // Grid configuration
  columns?: {
    base?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };

  // Thumbnail styling
  cornerRadius?: ThumbnailRadius;
  aspectRatio?: string;
  hoverScale?: boolean;
  showTitle?: boolean;
  titleClassName?: string;
  showVideoOverlay?: boolean;
  useVideoThumbnail?: boolean;
  loading?: "lazy" | "eager";
}

// Default responsive columns
const DEFAULT_COLUMNS = {
  base: 2,
  sm: 3,
  md: 4,
  lg: 5,
};

// Static class map to avoid Tailwind purge issues with dynamic class generation
const GRID_COLUMN_CLASSES = {
  base: {
    1: "grid-cols-1",
    2: "grid-cols-2",
    3: "grid-cols-3",
    4: "grid-cols-4",
    5: "grid-cols-5",
    6: "grid-cols-6",
  },
  sm: {
    1: "sm:grid-cols-1",
    2: "sm:grid-cols-2",
    3: "sm:grid-cols-3",
    4: "sm:grid-cols-4",
    5: "sm:grid-cols-5",
    6: "sm:grid-cols-6",
  },
  md: {
    1: "md:grid-cols-1",
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4",
    5: "md:grid-cols-5",
    6: "md:grid-cols-6",
  },
  lg: {
    1: "lg:grid-cols-1",
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3",
    4: "lg:grid-cols-4",
    5: "lg:grid-cols-5",
    6: "lg:grid-cols-6",
  },
  xl: {
    1: "xl:grid-cols-1",
    2: "xl:grid-cols-2",
    3: "xl:grid-cols-3",
    4: "xl:grid-cols-4",
    5: "xl:grid-cols-5",
    6: "xl:grid-cols-6",
    7: "xl:grid-cols-7",
    8: "xl:grid-cols-8",
  },
} as const;

const buildGridClasses = (columns: StashThumbnailGridProps["columns"] = DEFAULT_COLUMNS) => {
  const classes: string[] = ["stash-gallery", "grid", "items-start"];

  if (columns.base) classes.push(GRID_COLUMN_CLASSES.base[columns.base as keyof typeof GRID_COLUMN_CLASSES.base]);
  if (columns.sm) classes.push(GRID_COLUMN_CLASSES.sm[columns.sm as keyof typeof GRID_COLUMN_CLASSES.sm]);
  if (columns.md) classes.push(GRID_COLUMN_CLASSES.md[columns.md as keyof typeof GRID_COLUMN_CLASSES.md]);
  if (columns.lg) classes.push(GRID_COLUMN_CLASSES.lg[columns.lg as keyof typeof GRID_COLUMN_CLASSES.lg]);
  if (columns.xl) classes.push(GRID_COLUMN_CLASSES.xl[columns.xl as keyof typeof GRID_COLUMN_CLASSES.xl]);

  return classes.join(" ");
};


export const StashThumbnailGrid = memo(
  ({
    posts,
    className,
    emptyMessage = "No items to display",
    columns = DEFAULT_COLUMNS,
    cornerRadius = "2xl",
    aspectRatio = "aspect-[9/16]",
    hoverScale = true,
    showTitle,
    titleClassName,
    showVideoOverlay = true,
    useVideoThumbnail = false,
    loading = "lazy",
    isEditMode,
    selectedIds = new Set(),
    onToggleSelect,
    unavailableIds,
    onRetryItem,
    getItemFooter,
    getItemOverlay,
    getItemClassName,
    collectionContext,
    appendNode,
  }: StashThumbnailGridProps) => {
    usePreloadStashes(posts);

    const gridClasses = useMemo(() => buildGridClasses(columns), [columns]);

    if (posts.length === 0) {
      return (
        <div className="flex h-full w-full items-center justify-center p-4">
          <div className={cn(META, "text-center")}>{emptyMessage}</div>
        </div>
      );
    }

    return (
      <div className={cn(gridClasses, className)}>
        {posts.map(post => (
          <StashThumbnail
            key={post.id}
            post={post}
            width="full"
            cornerRadius={cornerRadius}
            aspectRatio={aspectRatio}
            hoverScale={hoverScale}
            showTitle={showTitle}
            titleClassName={titleClassName}
            showVideoOverlay={showVideoOverlay}
            useVideoThumbnail={useVideoThumbnail}
            loading={loading}
            {...buildThumbnailItemProps(post, {
              isEditMode,
              selectedIds,
              onToggleSelect,
              unavailableIds,
              onRetryItem,
              getItemFooter,
              getItemOverlay,
              getItemClassName,
              collectionContext,
            })}
          />
        ))}
        {appendNode}
      </div>
    );
  },
);

StashThumbnailGrid.displayName = "StashThumbnailGrid";

// Single item export for virtualized lists or custom layouts
export const StashThumbnailGridItem = memo(
  ({
    post,
    cornerRadius = "2xl",
    aspectRatio = "aspect-[9/16]",
    hoverScale = true,
    showTitle,
    titleClassName,
    showVideoOverlay = true,
    useVideoThumbnail = false,
    loading = "lazy",
    isEditMode = false,
    isSelected = false,
    onToggleSelect,
  }: {
    post: StashThumbnailData;
    cornerRadius?: ThumbnailRadius;
    aspectRatio?: string;
    hoverScale?: boolean;
    showTitle?: boolean;
    titleClassName?: string;
    showVideoOverlay?: boolean;
    useVideoThumbnail?: boolean;
    loading?: "lazy" | "eager";
    isEditMode?: boolean;
    isSelected?: boolean;
    onToggleSelect?: (postId: string) => void;
  }) => (
    <StashThumbnail
      post={post}
      href={`/stash/${post.id}`}
      width="full"
      cornerRadius={cornerRadius}
      aspectRatio={aspectRatio}
      hoverScale={hoverScale}
      showTitle={showTitle}
      titleClassName={titleClassName}
      showVideoOverlay={showVideoOverlay}
      useVideoThumbnail={useVideoThumbnail}
      loading={loading}
      isEditMode={isEditMode}
      isSelected={isSelected}
      onToggleSelect={onToggleSelect}
    />
  ),
);

StashThumbnailGridItem.displayName = "StashThumbnailGridItem";
